import React, { useState, useEffect, useRef } from 'react';
import cronstrue from 'cronstrue';

const TextField = (props) => {
  const {
    id,
    label,
    name,
    value = '',
    onChange,
    labelClass,
    isDynamic = false,
    placeholder = '',
    required = false,
    disabled = false,
    hint, // ✅ Hint popup support
    showCronDescription = false,
  } = props;
  // console.log("props", props);
  const [textValues, setTextValues] = useState(
    isDynamic ? (Array.isArray(value) ? value : []) : value
  );
  const [showHint, setShowHint] = useState(false);
  const hintRef = useRef(null);
  const labelClasses = `${labelClass || ''} mb-0`;

  /* ---------------- SYNC VALUE ---------------- */
  useEffect(() => {
    if (!isDynamic) {
      setTextValues(value || '');
    } else {
      setTextValues(Array.isArray(value) ? value : []);
    }
  }, [value, isDynamic]);

  /* ---------------- CLOSE HINT ON OUTSIDE CLICK ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hintRef.current && !hintRef.current.contains(e.target)) {
        setShowHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleSingleValueChange = (newValue) => {
    setTextValues(newValue);
    onChange(id, newValue);
  };

  const handleArrayValueChange = (index, newValue) => {
    const updatedValues = [...textValues];
    updatedValues[index] = newValue;
    setTextValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleAddField = () => {
    const updatedValues = [...textValues, ''];
    setTextValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleRemoveField = (index) => {
    const updatedValues = textValues.filter((_, i) => i !== index);
    setTextValues(updatedValues);
    onChange(id, updatedValues);
  };

  const getCronDescription = (cronValue) => {
    if (!cronValue) return '';
    try {
      return cronstrue.toString(cronValue);
    } catch (error) {
      return 'Invalid cron expression';
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="form-group">
      {label && (
        <div className="d-inline-flex align-items-center gap-2 mb-1 position-relative flex-nowrap">
          <label htmlFor={name} className={labelClasses}>
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>

          {hint && (
            <div className="position-relative" ref={hintRef}>
              <span
                role="button"
                tabIndex={0}
                className="text-muted"
                style={{ cursor: 'pointer' }}
                aria-label="Show hint"
                onClick={() => setShowHint((prev) => !prev)}
                onKeyDown={(e) => e.key === 'Enter' && setShowHint((prev) => !prev)}
              >
                ℹ️
              </span>

              {showHint && (
                <div
                  className="position-absolute bg-white border shadow rounded p-2"
                  style={{
                    top: '120%',
                    left: 0,
                    width: '260px',
                    zIndex: 3000,
                  }}
                >
                  <div className="small text-muted">{hint}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isDynamic ? (
        <>
          {textValues.length === 0 ? (
            <div className="d-flex align-items-center mb-2">
              <input
                type="text"
                id={`${name}-placeholder`}
                name={`${name}-placeholder`}
                className="form-control"
                placeholder={placeholder || 'No fields yet'}
                value=""
                required={required}
                disabled={disabled || true}
                readOnly
              />
              <button
                type="button"
                className="btn btn-primary ms-2"
                onClick={handleAddField}
                disabled={disabled}
              >
                Add Field
              </button>
            </div>
          ) : (
            textValues.map((val, index) => (
              <div key={index} className="d-flex align-items-center mb-2">
                <input
                  type="text"
                  id={`${name}-${index}`}
                  name={`${name}[${index}]`}
                  className="form-control"
                  placeholder={placeholder || `Enter value ${index + 1}`}
                  value={val}
                  required={required}
                  disabled={disabled}
                  onChange={(e) =>
                    handleArrayValueChange(index, e.target.value)
                  }
                />
                <button
                  type="button"
                  className="btn btn-danger ms-2"
                  onClick={() => handleRemoveField(index)}
                  disabled={disabled}
                >
                  Remove
                </button>
              </div>
            ))
          )}

          {textValues.length > 0 && (
            <button
              type="button"
              className="btn btn-primary mt-2"
              onClick={handleAddField}
              disabled={disabled}
            >
              Add Field
            </button>
          )}
        </>
      ) : (
        <>
          <input
            type="text"
            id={name}
            name={name}
            className="form-control"
            placeholder={placeholder}
            value={textValues}
            required={required}
            disabled={disabled}
            onChange={(e) => handleSingleValueChange(e.target.value)}
          />
          {showCronDescription && (
            <small className="form-text text-muted d-block mt-1">
              {getCronDescription(textValues)}
            </small>
          )}
        </>
      )}
    </div>
  );
};

export default TextField;
